import {usePDPSearchParams} from './use-pdp-search-params'
import {useCallback, useMemo} from 'react'
import {useHistory, useLocation} from 'react-router-dom'

/**
 * Hook to manage the selected state of a product in a set
 *
 * **Warning:** This hook is tightly coupled with useStoredConfiguration hook,
 * so any changes should be tested in conjunction with useStoredConfiguration
 *
 * @param {object} product
 * @return {[boolean,(function(boolean?): void)]}
 */
const useSelectableSetProduct = (product) => {
    const history = useHistory()
    const {pathname} = useLocation()
    const [allParams, productParams] = usePDPSearchParams(product?.setProductId)
    // directly compute the selected state from the search params as they update much quicker than the product which needs to be fetched
    const isSelected = useMemo(() => productParams.get('selected') === '1', [productParams])
    const isSelectionChanging = useMemo(
        () => isSelected !== product?.isSetProductSelected ?? false,
        [isSelected, product]
    )

    const setSelected = useCallback(
        (newState) => {
            // without product, do nothing
            if (!product) {
                return
            }

            // If the new state is the same as the current state, do nothing
            if (newState === isSelected) {
                return
            }

            // toggle the selected state
            if (isSelected) {
                productParams.delete('selected')
            } else {
                productParams.set('selected', '1')
            }
            if (productParams.size > 0) {
                allParams.set(product.setProductId, productParams.toString())
            } else {
                allParams.delete(product.setProductId)
            }
            history.replace(`${pathname}${allParams.size > 0 ? `?${allParams.toString()}` : ''}`)
        },
        [allParams, history, isSelected, pathname, product, productParams]
    )

    return [isSelected, setSelected, isSelectionChanging]
}

export default useSelectableSetProduct
